import { Suspense, useMemo } from "react";
import { Await, LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { NextFilterAndSortListings } from "@/pages/root/shop/NextFilterAndSortListings";
import { useShopDiscounts } from "@/hooks/useShopDiscounts";
import { ALGOLIA_PRODUCT_TYPES } from "@/utils/algoliaCatalog";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import type { Models } from "api/types";
import { waitObj } from "toolbox/Promise";
import type { AwaitedObject } from "toolbox/Promise";

import { CatalogMembershipCard } from "./CatalogMembershipCard";
import { loadShopMemberships } from "./loaders";
import { ShopListHeader } from "./ShopListHeader/ShopListHeader";
import { ShopListingSkeleton } from "./ShopListingSkeleton";
import type { LeadingItem } from "./NextFilterAndSortListings";

type LoaderData = AwaitedObject<ReturnType<typeof loadShopMemberships>>;

export const shopMembershipsLoader: LoaderFunction = async () => {
  const data = loadShopMemberships();
  const platform = await data.platform;

  if (platform.platformSettings?.membershipsEnabled === false) {
    return redirect("/home");
  }

  return { data: waitObj(data) };
};

const ShopMembershipsContent = ({ data }: { data: LoaderData }) => {
  const navigate = useNavigate();
  const {
    platform,
    shopData,
    categories,
    customShopCategories,
    user,
    platformLocationId,
    recommendationIds,
    areRetailProductsVisible,
    isSkinDropVisible,
    memberships,
    catalog,
  } = data;

  const shopDiscounts = useShopDiscounts();
  const recommendedIds = new Set(recommendationIds);
  const membershipsById = useMemo(
    () => new Map(memberships.map((membership) => [membership.id, membership])),
    [memberships],
  );

  const unlockedLeadingItems = useMemo((): LeadingItem[] => {
    return memberships
      .filter((membership) => membership.unlisted)
      .map((membership) => ({
        listing: { id: membership.id } as Models<"ListingModel">,
        node: (
          <CatalogMembershipCard
            membership={membership}
            shopDiscounts={shopDiscounts}
            customShopCategories={customShopCategories}
            onClick={() => navigate(`/pdp/membership/${membership.id}`)}
            guestMode={user.isGuest}
            isRecommended={recommendedIds.has(membership.id)}
          />
        ),
      }));
  }, [customShopCategories, memberships, navigate, recommendedIds, shopDiscounts, user.isGuest]);

  const membershipCount =
    (catalog.productTypeCounts[ALGOLIA_PRODUCT_TYPES.membership] ?? 0) +
    unlockedLeadingItems.length;

  const membershipsCategory = categories.find((c) => c.defaultCategoryId === "memberships");

  return (
    <div className="flex flex-col bg-two">
      {membershipsCategory && (
        <ShopListHeader
          title={membershipsCategory.title}
          subtitle={membershipsCategory.description}
          bgGradient
          backgroundColor="rgb(var(--repeatmd-brand-color))"
          backgroundPattern
        />
      )}
      {membershipCount > 0 && (
        <div className="mx-auto flex w-full max-w-6xl flex-col px-4 py-5 desk:px-6 desklg:px-8 desklg:py-10">
          <NextFilterAndSortListings
            surface="memberships"
            types={["Membership"]}
            areas={shopData.areas ?? []}
            concerns={shopData.concerns ?? []}
            platformId={platform.id}
            platformLocationId={platformLocationId}
            visibility={{ areRetailProductsVisible, isSkinDropVisible }}
            shopDiscounts={shopDiscounts}
            leadingItems={unlockedLeadingItems}
            hitFilter={(hit) => membershipsById.has(hit.objectID)}
          >
            {(hit) => {
              const membership = membershipsById.get(hit.objectID)!;
              return (
                <CatalogMembershipCard
                  membership={membership}
                  shopDiscounts={shopDiscounts}
                  customShopCategories={customShopCategories}
                  onClick={() => navigate(`/pdp/membership/${membership.id}`)}
                  guestMode={user.isGuest}
                  isRecommended={recommendedIds.has(membership.id)}
                />
              );
            }}
          </NextFilterAndSortListings>
        </div>
      )}
      <RepeatMDFooter />
    </div>
  );
};

const ShopMembershipsPage = () => {
  const { data } = useLoaderData() as { data: Promise<LoaderData> };
  return (
    <Suspense fallback={<ShopListingSkeleton />}>
      <Await resolve={data}>{(resolved) => <ShopMembershipsContent data={resolved} />}</Await>
    </Suspense>
  );
};

export default ShopMembershipsPage;
